import AddIcon from '@mui/icons-material/Add';
import CachedIcon from '@mui/icons-material/Cached';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import ViewWeekOutlinedIcon from '@mui/icons-material/ViewWeekOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slider,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { FC, useEffect, useState } from 'react';
import Colors from '../../utils/color';

/** Default pane split (Left / Middle / Right), as percentages summing to 100. */
export const DEFAULT_PANE_SIZES: [number, number, number] = [25, 50, 25];
/**
 * Per-pane clamp — mirrors the PanelGroup min/maxSize so typed + dragged agree.
 * Every pane keeps at least 25%; with three panes that caps any one at 50%.
 */
export const MIN_PANE_SIZE = 25;
export const MAX_PANE_SIZE = 50;

// All three sliders share one accent — the app primary — so the dialog reads
// as neutral controls rather than a rainbow.
const PANES: { label: string }[] = [
  { label: 'Left' },
  { label: 'Middle' },
  { label: 'Right' },
];

interface PanePreset {
  label: string;
  sizes: [number, number, number];
}

// "Wide X" = that pane at 50%, the other two at 25% each.
const PANE_PRESETS: PanePreset[] = [
  { label: 'Balanced', sizes: [33, 34, 33] },
  { label: 'Wide transcript', sizes: [50, 25, 25] },
  { label: 'Wide menu', sizes: [25, 50, 25] },
  { label: 'Wide cart', sizes: [25, 25, 50] },
];

// Single source of truth for the section headings ("LAYOUT PREFERENCE" and
// "QUICK PRESETS") so they render identically — same as the LIVE TRANSCRIPT
// heading. Applied via sx to BOTH (not makeStyles + a raw <span>), and it pins
// fontFamily explicitly so neither can fall back to the system font.
const sectionHeadingSx = (theme: Theme) => ({
  color: Colors.readBack,
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.8rem',
  fontWeight: 700,
  letterSpacing: '0.04em',
  textTransform: 'uppercase' as const,
});

// Shared style for the matching status labels — the "Total: N%" banner label and
// the "Reset to default" button — so they render identically (size, weight,
// family, spacing, no caps). Colour is applied per-usage (green ok / red error).
const statusLabelSx = (theme: Theme) => ({
  fontFamily: theme.typography.fontFamily,
  fontSize: '0.75rem',
  fontWeight: 700,
  letterSpacing: 'normal',
  textTransform: 'none' as const,
});

interface LayoutPreferenceDialogProps {
  open: boolean;
  onClose: () => void;
  /** Live pane sizes (percentages) — reflects manual dragging when opened. */
  sizes: number[];
  onApply: (sizes: [number, number, number]) => void;
  onReset: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  titleBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
    padding: theme.spacing(2, 3, 1),
  },
  titleLeft: {
    display: 'flex',
    alignItems: 'center',
    // Same tight icon↔label gap as the LIVE TRANSCRIPT header.
    gap: theme.spacing(0.75),
  },
  titleIcon: {
    // Small inline icon in the transcript-blue, mirroring the LIVE TRANSCRIPT
    // header (no filled circle).
    color: Colors.readBack,
    fontSize: 18,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  },
  // Type scale — body is 0.75rem, the app's standard text size (header session
  // id, restaurant name/code, dropdown options all use it):
  //   section headings  0.8rem  / 700 / uppercase / blue  -> sectionHeadingSx
  //   pane labels       0.75rem / 600
  //   body              0.75rem / 400   (descriptions, hints, %, preset labels)
  //   pane value        1rem    / 700   (the one prominent stepper number)
  // Only color/line-height here — font-size is set via sx on the element
  // (makeStyles loses to MUI's Typography defaults; see the note in the JSX).
  desc: {
    color: theme.palette.text.secondary,
    lineHeight: 1.5,
  },
  paneRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    // Section gap — same value used before "Quick presets".
    marginTop: theme.spacing(3),
  },
  paneCard: {
    flex: 1,
    minWidth: 0,
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(1.5),
    // Compact — was spacing(2); the cards were reading too large.
    padding: theme.spacing(1.25, 1),
    textAlign: 'center',
  },
  stepperRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.5),
  },
  stepBtn: {
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(1),
  },
  paneValue: {
    // The one prominent number in the dialog — kept a touch larger than body.
    fontSize: '1rem',
    fontWeight: 700,
    minWidth: 30,
  },
  pct: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
  },
  // Total banner — one block in the stack; same 24px gap above as every other.
  totalBar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3),
    padding: theme.spacing(1.25, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: '#F4F6F8',
  },
  totalIconOk: {
    color: Colors.welcome,
  },
  totalIconError: {
    color: theme.palette.error.main,
  },
  totalHint: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
  },
  presetRow: {
    display: 'flex',
    // All four presets share one row, each an equal-width column card.
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
  },
  presetCard: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1.25, 1),
    border: `1px solid ${Colors.alto}`,
    borderRadius: theme.spacing(1.5),
    backgroundColor: Colors.white,
    cursor: 'pointer',
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      backgroundColor: Colors.porcelain,
    },
  },
  presetActive: {
    borderColor: theme.palette.primary.main,
    boxShadow: `inset 0 0 0 1px ${theme.palette.primary.main}`,
  },
  presetLabel: {
    fontSize: '0.75rem',
    // Regular weight — the preset cards shouldn't shout.
    fontWeight: theme.typography.fontWeightRegular,
    textAlign: 'center',
    lineHeight: 1.25,
    // Wrap (never clip) so long labels like "Wide transcript" stay readable
    // while all four presets share one row.
    maxWidth: '100%',
  },
  glyph: {
    display: 'flex',
    gap: 2,
    width: 30,
    height: 20,
    flexShrink: 0,
  },
  glyphBar: {
    backgroundColor: Colors.daintree,
    borderRadius: 2,
  },
}));

/**
 * Rendered inside MainPageLayout so it can drive the PanelGroup directly.
 * Editing the values resizes the panes (via `onApply` -> `setLayout`); opening
 * the dialog reflects the latest live sizes so a manual drag shows up here too.
 */
const LayoutPreferenceDialog: FC<LayoutPreferenceDialogProps> = ({
  open,
  onClose,
  sizes,
  onApply,
  onReset,
}) => {
  const classes = useStyles();
  const [values, setValues] = useState<number[]>(() =>
    DEFAULT_PANE_SIZES.slice()
  );

  // Reflect the latest live sizes (including manual drags) each time it opens.
  useEffect(() => {
    if (open) {
      setValues(sizes.map((size) => Math.round(size)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const total = values.reduce((sum, value) => sum + (value || 0), 0);
  const inRange = values.every(
    (value) => value >= MIN_PANE_SIZE && value <= MAX_PANE_SIZE
  );
  const isValid = total === 100 && inRange;

  const clamp = (value: number) =>
    Math.min(MAX_PANE_SIZE, Math.max(MIN_PANE_SIZE, value));

  const setValue = (index: number, value: number) => {
    const next = [...values];
    next[index] = value;
    setValues(next);
  };

  const activePreset = PANE_PRESETS.find((preset) =>
    preset.sizes.every((size, index) => size === values[index])
  );

  const handleApply = () => {
    if (!isValid) return;
    onApply([values[0], values[1], values[2]]);
    onClose();
  };

  const handleReset = () => {
    setValues(DEFAULT_PANE_SIZES.slice());
    onReset();
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      // index.css sets `body` to the system font, and only MUI Typography/Button
      // pull in the theme font — so the plain <span>/<button> text here would
      // otherwise render in the system font. Force the app font ('Prompt') on the
      // dialog paper so every element inherits it consistently.
      PaperProps={{
        sx: (theme) => ({ fontFamily: theme.typography.fontFamily }),
      }}
      // Marks the dialog root so the AI auto-mode intercept listener in
      // AIStatusBar ignores interactions here (resizing/presets aren't a
      // "take over the order" action).
      data-layout-preference="true"
    >
      <Box className={classes.titleBar}>
        <div className={classes.titleLeft}>
          <div className={classes.titleIcon}>
            <ViewWeekOutlinedIcon fontSize="inherit" />
          </div>
          <Typography sx={sectionHeadingSx}>Layout preference</Typography>
        </div>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* sx (not makeStyles) for padding/margins/fonts on every MUI component
          below: @mui/styles (JSS) injects before MUI's emotion styles, so a
          makeStyles class loses to MUI's own defaults (padding, font-size,
          font-weight, margin). sx wins. */}
      <DialogContent sx={{ px: 3, pt: 1, pb: 0 }}>
        <Typography className={classes.desc} sx={{ fontSize: '0.75rem' }}>
          Adjust the width of each pane as a percentage.
        </Typography>
        <Typography className={classes.desc} sx={{ fontSize: '0.75rem' }}>
          The three values must add up to 100% (each between {MIN_PANE_SIZE}%
          and {MAX_PANE_SIZE}%).
        </Typography>

        <div className={classes.paneRow}>
          {PANES.map((pane, index) => (
            <div key={pane.label} className={classes.paneCard}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, mb: 1 }}>
                {pane.label}
              </Typography>
              <div className={classes.stepperRow}>
                <IconButton
                  className={classes.stepBtn}
                  size="small"
                  onClick={() => setValue(index, clamp(values[index] - 1))}
                  disabled={values[index] <= MIN_PANE_SIZE}
                  aria-label={`Decrease ${pane.label}`}
                >
                  <RemoveIcon fontSize="small" />
                </IconButton>
                <span
                  className={classes.paneValue}
                  data-testid={`pane-size-${pane.label.toLowerCase()}`}
                >
                  {values[index]}
                </span>
                <span className={classes.pct}>%</span>
                <IconButton
                  className={classes.stepBtn}
                  size="small"
                  onClick={() => setValue(index, clamp(values[index] + 1))}
                  disabled={values[index] >= MAX_PANE_SIZE}
                  aria-label={`Increase ${pane.label}`}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </div>
              <Slider
                size="small"
                value={values[index]}
                min={MIN_PANE_SIZE}
                max={MAX_PANE_SIZE}
                step={1}
                onChange={(_event, value) => setValue(index, value as number)}
                // py tightens the slider's default touch-padding so the card
                // doesn't have airy empty space below the track.
                sx={(theme) => ({
                  color: theme.palette.primary.main,
                  mt: 0.5,
                  py: 0.75,
                })}
                aria-label={`${pane.label} width`}
              />
            </div>
          ))}
        </div>

        <div className={classes.totalBar}>
          {isValid ? (
            <CheckCircleIcon className={classes.totalIconOk} />
          ) : (
            <ErrorOutlineIcon className={classes.totalIconError} />
          )}
          <Typography
            component="span"
            sx={(theme) => ({
              ...statusLabelSx(theme),
              color: isValid ? Colors.welcome : theme.palette.error.main,
            })}
          >
            Total: {total}%
          </Typography>
          <span className={classes.totalHint}>
            {isValid
              ? 'All good! Your layout adds up to 100%.'
              : 'The three values must add up to 100%.'}
          </span>
        </div>

        <Typography
          sx={(theme) => ({ ...sectionHeadingSx(theme), mt: 3, mb: 1.5 })}
        >
          Quick presets
        </Typography>
        <div className={classes.presetRow}>
          {PANE_PRESETS.map((preset) => {
            const active = preset.label === activePreset?.label;
            const widest = Math.max(...preset.sizes);
            const hasWide = preset.sizes.some((size) => size >= 45);
            return (
              <button
                key={preset.label}
                type="button"
                onClick={() => setValues(preset.sizes.slice())}
                className={`${classes.presetCard} ${
                  active ? classes.presetActive : ''
                }`}
              >
                <span className={classes.glyph}>
                  {preset.sizes.map((size, index) => (
                    <span
                      key={PANES[index].label}
                      className={classes.glyphBar}
                      style={{
                        flexGrow: size,
                        opacity: hasWide ? (size === widest ? 1 : 0.3) : 0.9,
                      }}
                    />
                  ))}
                </span>
                <span className={classes.presetLabel}>{preset.label}</span>
              </button>
            );
          })}
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, pt: 3, pb: 2 }}>
        <Button
          // Matches the green "Total: 100%" label exactly (shared statusLabelSx).
          sx={(theme) => ({ ...statusLabelSx(theme), color: Colors.welcome })}
          onClick={handleReset}
          startIcon={<CachedIcon />}
        >
          Reset to default
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleApply} disabled={!isValid}>
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LayoutPreferenceDialog;
